import { useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { BankIcon, ReceiptIcon, CheckCircleIcon, FileTextIcon, CalculatorIcon, ArrowsLeftRightIcon } from "@phosphor-icons/react";
import ReceivablesTab from "@/pages/_payments/ReceivablesTab";
import RTCPaymentsTab from "@/pages/_payments/RTCPaymentsTab";
import ReceiptsTab from "@/pages/_payments/ReceiptsTab";
import CargosMoratoriosTab from "@/pages/_payments/CargosMoratoriosTab";
import AutorizacionesTab from "@/pages/_payments/AutorizacionesTab";
import { useSearchParams } from "react-router-dom";

/**
 * Módulo 6 — Pagos y Cobranza (unificado)
 * Pestañas (PAG-05):
 *  - Cuentas por Cobrar (agrupado por cliente · cobro multi-aplicación)
 *  - Cobros RTC (autorización admin + GRATIVA)
 *  - Recibos del mes (PDF imprimible + filtro de rango de fechas)
 */
export default function PaymentsAndReceivables() {
  const { user, hasPermission } = useAuth();
  const canAuthorize = user?.role === "admin" || hasPermission?.("payments.authorize");
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(["cobros", "recibos", "moratorios", "autorizaciones"].includes(sp.get("tab")) ? sp.get("tab") : "cxc");
  // Cargos moratorios (informativo): Administrador, Delegado, Solo lectura (Lector) y RTC
  const eff = user?.effective || {};
  const verMoratorios = Boolean(eff.is_admin || (eff.roles || []).some((r) => ["delegado", "solo_lectura", "ventas_rtc"].includes(r)));
  // Autorizaciones de cambio de proyecto: Administrador (resuelve) y Delegado (consulta sus solicitudes)
  const verAutorizaciones = Boolean(eff.is_admin || (eff.roles || []).includes("delegado"));

  return (
    <Layout
      title="Pagos y Cobranza"
      subtitle="Cuentas por Cobrar · Cobros · Recibos generados · Cargos moratorios"
    >
      <div className="flex gap-2 mb-5 border-b border-border/60 overflow-x-auto" data-testid="payments-tabs">
        <TabBtn active={tab === "cxc"} onClick={() => setTab("cxc")} icon={BankIcon} testid="tab-cxc">
          Cuentas por Cobrar
        </TabBtn>
        <TabBtn active={tab === "cobros"} onClick={() => setTab("cobros")} icon={ReceiptIcon} testid="tab-cobros">
          Cobros {canAuthorize ? "(autorización)" : "RTC"}
        </TabBtn>
        <TabBtn active={tab === "recibos"} onClick={() => setTab("recibos")} icon={FileTextIcon} testid="tab-recibos">
          Recibos del mes
        </TabBtn>
        {verAutorizaciones && (
          <TabBtn active={tab === "autorizaciones"} onClick={() => setTab("autorizaciones")} icon={ArrowsLeftRightIcon} testid="tab-autorizaciones">
            Autorizaciones
          </TabBtn>
        )}
        {verMoratorios && (
          <TabBtn active={tab === "moratorios"} onClick={() => setTab("moratorios")} icon={CalculatorIcon} testid="tab-moratorios">
            Cargos moratorios
          </TabBtn>
        )}
      </div>

      {tab === "cxc" && <ReceivablesTab />}
      {tab === "cobros" && <RTCPaymentsTab />}
      {tab === "recibos" && <ReceiptsTab />}
      {tab === "moratorios" && verMoratorios && <CargosMoratoriosTab />}
      {tab === "autorizaciones" && verAutorizaciones && <AutorizacionesTab />}
    </Layout>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition-all ${
        active
          ? "border-agro-green text-agro-green"
          : "border-transparent text-text-muted hover:text-text-primary"
      }`}
    >
      <Icon size={14} /> {children}
    </button>
  );
}
